// Strategy API service types and methods
import { API_BASE } from '../env';
import { apiFetch } from '../auth/apiClient';
import type { ScannerResult, ScannerUpdate, FilterOperator, StrategyCategory } from './scannerService';

// Re-export StrategyCategory so consumers can keep importing from here
export type { StrategyCategory };

// ── Types matching backend models ──────────────────────────

export type StrategyFilterField =
  | 'Price'
  | 'ChangePercent'
  | 'ChangeFromOpen'
  | 'ChangePercentFromOpen'
  | 'GapPercent'
  | 'Volume'
  | 'AvgVolume'
  | 'RelativeVolume'
  | 'Float'
  | 'MarketCap'
  | 'ATR'
  | 'ATRPercent'
  | 'AtrSpd'
  | 'AtrVwap'
  | 'AtrHoD'
  | 'ZenP'
  | 'ZenV'
  | 'Change2Min'
  | 'ChangePercent2Min'
  | 'Week52High'
  | 'Week52Low'
  | 'DailySma20'
  | 'DailySma50'
  | 'DailySma200'
  | 'DistFromSma20Pct'
  | 'DistFromSma50Pct'
  | 'DistFromSma200Pct'
  | 'DistFrom52wHighPct'
  | 'DistFrom52wLowPct'
  | 'HighOfDay'
  | 'LowOfDay'
  | 'DistFromHodPct'
  | 'DistFromLodPct'
  | 'IsPreMarket'
  | 'IsPostMarket';

export interface StrategyFilter {
  field: StrategyFilterField;
  operator: FilterOperator;
  value: number;
  value2?: number | null;
}

export interface StrategyConfig {
  id: string;
  name: string;
  description: string;
  category: StrategyCategory;
  alerts: string[];           // AlertTypeCode values
  filters: StrategyFilter[];
  exchanges: string[];
  symbolList: string[];
  columns: any[];
  sort?: any;
  maxResults: number;
  isPreset: boolean;
  isActive: boolean;
  createdAt: string;
  lastUpdated?: string;
  alertAction?: any;
  entryMaxAgeMinutes?: number; // TTL for strategy entries (null = permanent)
  atrPeriod?: number;          // ATR lookback (default 14)
  smaPeriod?: number;          // SMA lookback (0 = disabled)
  type?: string;               // ScannerType when coming from unified backend
}

/** @deprecated Backend now returns ScannerResult[] directly from strategy endpoints */
export interface StrategyWindowEntry {
  result: ScannerResult;
  triggers: AlertTrigger[];
  entryTime: string;
}

export interface AlertTrigger {
  id: string;
  alertId: string;
  alertName: string;
  symbol: string;
  message: string;
  price?: number;
  timestamp: string;
  isRead: boolean;
  data: Record<string, any>;
}

export interface AlertTypeDefinition {
  code: string;
  name: string;
  description: string;
  category: string;
  direction: string;
}

export interface StrategyEnableRequest {
  symbols: string[];
  customConfig?: StrategyConfig;
}

// ── Panel-strategy association ─────────────────────────────

export interface PanelStrategy {
  strategyId: string;
  name: string;
  color: string;
  enabled: boolean;
}

// ── Strategy color palette (distinct, high-contrast) ───────

export const STRATEGY_COLORS: string[] = [
  '#4ade80', // green-400
  '#f87171', // red-400
  '#60a5fa', // blue-400
  '#facc15', // yellow-400
  '#c084fc', // purple-400
  '#fb923c', // orange-400
  '#2dd4bf', // teal-400
  '#f472b6', // pink-400
  '#a3e635', // lime-400
  '#38bdf8', // sky-400
  '#e879f9', // fuchsia-400
  '#fbbf24', // amber-400
];

// ── Filter field metadata for the UI ───────────────────────

export interface FilterFieldMeta {
  field: StrategyFilterField;
  label: string;
  unit?: string;
  step?: number;
  min?: number;
  max?: number;
  category: 'Price' | 'Volume' | 'Fundamentals' | 'Volatility' | 'Momentum' | 'Historical' | 'Intraday' | 'Session';
}

export const STRATEGY_FILTER_FIELDS: FilterFieldMeta[] = [
  // Price
  { field: 'Price', label: 'Price', unit: '$', step: 0.01, min: 0, category: 'Price' },
  { field: 'ChangePercent', label: 'Change %', unit: '%', step: 0.1, category: 'Price' },
  { field: 'ChangeFromOpen', label: 'Change from Open', unit: '$', step: 0.01, category: 'Price' },
  { field: 'ChangePercentFromOpen', label: 'Change % from Open', unit: '%', step: 0.1, category: 'Price' },
  { field: 'GapPercent', label: 'Gap %', unit: '%', step: 0.1, category: 'Price' },
  // Volume
  { field: 'Volume', label: 'Volume', step: 1000, min: 0, category: 'Volume' },
  { field: 'AvgVolume', label: 'Avg Volume', step: 1000, min: 0, category: 'Volume' },
  { field: 'RelativeVolume', label: 'Relative Volume', step: 0.1, min: 0, category: 'Volume' },
  // Fundamentals
  { field: 'Float', label: 'Float', step: 100000, min: 0, category: 'Fundamentals' },
  { field: 'MarketCap', label: 'Market Cap', step: 1000000, min: 0, category: 'Fundamentals' },
  // Volatility
  { field: 'ATR', label: 'ATR', unit: '$', step: 0.01, min: 0, category: 'Volatility' },
  { field: 'ATRPercent', label: 'ATR %', unit: '%', step: 0.1, min: 0, category: 'Volatility' },
  { field: 'AtrSpd', label: 'ATR Speed', step: 0.1, category: 'Volatility' },
  { field: 'AtrVwap', label: 'ATR from VWAP', step: 0.1, category: 'Volatility' },
  { field: 'AtrHoD', label: 'ATR from HOD', step: 0.1, category: 'Volatility' },
  // Momentum
  { field: 'ZenP', label: 'ZenP', step: 0.1, category: 'Momentum' },
  { field: 'ZenV', label: 'ZenV', step: 0.1, category: 'Momentum' },
  { field: 'Change2Min', label: '2-Min Change', unit: '$', step: 0.01, category: 'Momentum' },
  { field: 'ChangePercent2Min', label: '2-Min Change %', unit: '%', step: 0.1, category: 'Momentum' },
  // Historical
  { field: 'Week52High', label: '52-Week High', unit: '$', step: 0.01, category: 'Historical' },
  { field: 'Week52Low', label: '52-Week Low', unit: '$', step: 0.01, category: 'Historical' },
  { field: 'DailySma20', label: 'SMA 20', unit: '$', step: 0.01, category: 'Historical' },
  { field: 'DailySma50', label: 'SMA 50', unit: '$', step: 0.01, category: 'Historical' },
  { field: 'DailySma200', label: 'SMA 200', unit: '$', step: 0.01, category: 'Historical' },
  { field: 'DistFromSma20Pct', label: '% from SMA 20', unit: '%', step: 0.1, category: 'Historical' },
  { field: 'DistFromSma50Pct', label: '% from SMA 50', unit: '%', step: 0.1, category: 'Historical' },
  { field: 'DistFromSma200Pct', label: '% from SMA 200', unit: '%', step: 0.1, category: 'Historical' },
  { field: 'DistFrom52wHighPct', label: '% from 52w High', unit: '%', step: 0.1, category: 'Historical' },
  { field: 'DistFrom52wLowPct', label: '% from 52w Low', unit: '%', step: 0.1, category: 'Historical' },
  // Intraday
  { field: 'HighOfDay', label: 'High of Day', unit: '$', step: 0.01, category: 'Intraday' },
  { field: 'LowOfDay', label: 'Low of Day', unit: '$', step: 0.01, category: 'Intraday' },
  { field: 'DistFromHodPct', label: '% from HOD', unit: '%', step: 0.1, category: 'Intraday' },
  { field: 'DistFromLodPct', label: '% from LOD', unit: '%', step: 0.1, category: 'Intraday' },
  // Session
  { field: 'IsPreMarket', label: 'Pre-Market', step: 1, min: 0, max: 1, category: 'Session' },
  { field: 'IsPostMarket', label: 'Post-Market', step: 1, min: 0, max: 1, category: 'Session' },
];

// Grouped for the UI
export const STRATEGY_FILTER_FIELDS_GROUPED = STRATEGY_FILTER_FIELDS.reduce<
  Record<string, FilterFieldMeta[]>
>((acc, f) => {
  (acc[f.category] ??= []).push(f);
  return acc;
}, {});

// ── Strategy category metadata ─────────────────────────────

export const STRATEGY_CATEGORIES: { value: StrategyCategory; label: string; color: string; icon: string }[] = [
  { value: 'Bullish', label: 'Bullish', color: 'bg-green-600', icon: '🟢' },
  { value: 'Bearish', label: 'Bearish', color: 'bg-red-600', icon: '🔴' },
  { value: 'Neutral', label: 'Neutral', color: 'bg-blue-600', icon: '🔵' },
  { value: 'PreMarket', label: 'Pre-Market', color: 'bg-yellow-600', icon: '🌅' },
  { value: 'PostMarket', label: 'Post-Market', color: 'bg-purple-600', icon: '🌙' },
  { value: 'Momentum', label: 'Momentum', color: 'bg-orange-600', icon: '⚡' },
  { value: 'Reversal', label: 'Reversal', color: 'bg-cyan-600', icon: '↩️' },
  { value: 'HaltResume', label: 'Halt/Resume', color: 'bg-yellow-500', icon: '⏸️' },
  { value: 'Custom', label: 'Custom', color: 'bg-gray-600', icon: '🔧' },
];

export const FILTER_OPERATORS: { value: FilterOperator; label: string; symbol: string }[] = [
  { value: 'GreaterThan', label: 'Greater than', symbol: '>' },
  { value: 'GreaterThanOrEqual', label: 'At least', symbol: '>=' },
  { value: 'LessThan', label: 'Less than', symbol: '<' },
  { value: 'LessThanOrEqual', label: 'At most', symbol: '<=' },
  { value: 'Equals', label: 'Equals', symbol: '=' },
  { value: 'Between', label: 'Between', symbol: '↔' },
];

// ── API Service ────────────────────────────────────────────

class StrategyApiService {
  // ── Presets ──

  async getPresets(): Promise<Record<StrategyCategory, StrategyConfig[]>> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/presets`);
    if (!response.ok) throw new Error('Failed to get strategy presets');
    return response.json();
  }

  async getPresetsByCategory(category: StrategyCategory): Promise<StrategyConfig[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/presets/${category}`);
    if (!response.ok) throw new Error('Failed to get strategy presets');
    return response.json();
  }

  async getCategories(): Promise<{ category: string; count: number }[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/categories`);
    if (!response.ok) throw new Error('Failed to get strategy categories');
    return response.json();
  }

  async getFilterFields(): Promise<{ field: string; description: string }[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/filters`);
    if (!response.ok) throw new Error('Failed to get strategy filter fields');
    return response.json();
  }

  // ── Alert Catalog ──

  async getAlertCatalog(): Promise<Record<string, AlertTypeDefinition>> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts/catalog`);
    if (!response.ok) throw new Error('Failed to get alert catalog');
    return response.json();
  }

  async getAlertCatalogGrouped(): Promise<Record<string, AlertTypeDefinition[]>> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts/catalog/grouped`);
    if (!response.ok) throw new Error('Failed to get alert catalog');
    return response.json();
  }

  async getAlertCategories(): Promise<{ value: string; name: string }[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/alerts/categories`);
    if (!response.ok) throw new Error('Failed to get alert categories');
    return response.json();
  }

  // ── Strategy Lifecycle ──

  async enable(strategyId: string, request: StrategyEnableRequest): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error('Failed to enable strategy');
    return response.json();
  }

  async disable(strategyId: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/disable`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to disable strategy');
    return response.json();
  }

  // ── Custom Strategy CRUD ──

  async getSaved(): Promise<StrategyConfig[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/saved`);
    if (!response.ok) throw new Error('Failed to get saved strategies');
    return response.json();
  }

  async create(config: StrategyConfig): Promise<StrategyConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!response.ok) throw new Error('Failed to create strategy');
    return response.json();
  }

  async update(strategyId: string, config: StrategyConfig): Promise<StrategyConfig> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!response.ok) throw new Error('Failed to update strategy');
    return response.json();
  }

  async deleteStrategy(strategyId: string): Promise<void> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete strategy');
  }

  async addSymbol(strategyId: string, symbol: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/symbols/${symbol}`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to add symbol');
    return response.json();
  }

  async removeSymbol(strategyId: string, symbol: string): Promise<any> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/symbols/${symbol}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to remove symbol');
    return response.json();
  }

  async getResults(strategyId: string, max: number = 50): Promise<ScannerResult[]> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/results?max=${max}`);
    if (!response.ok) throw new Error('Failed to get strategy results');
    return response.json();
  }

  async getStatus(strategyId: string): Promise<{
    strategyId: string;
    enabled: boolean;
    name?: string;
    category?: string;
    alerts: number;
    filters: number;
  }> {
    const response = await apiFetch(`${API_BASE}/api/Scanner/strategies/${strategyId}/status`);
    if (!response.ok) throw new Error('Failed to get strategy status');
    return response.json();
  }
}

export const strategyApiService = new StrategyApiService();
export default strategyApiService;
